import React, { useState, useEffect, useRef, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { PCFSoftShadowMap, ReinhardToneMapping, Object3D } from "three";
import { ResizeObserver } from "@juggle/resize-observer";

import { get } from "axios";
import City from "./model";
import "./App.css";

function CameraComp({ focus, isLoaded }) {
  let w = document.getElementById("react-root").offsetWidth;

  let defaultPos = w > 768 ? [0, -0.5, 12.5] : [0, -1, 23];
  let defaultRot = [0, 0, 0];
  let [cameraPos, setCameraPos] = useState(defaultPos);
  let [cameraRot, setCameraRot] = useState(defaultRot);
  const _c = useRef(new Object3D());

  useEffect(() => {
    if (!focus) {
      setCameraPos(defaultPos);
      setCameraRot(defaultRot);
    } else {
      const [x, y, z] = focus.position;
      const [rx, ry, rz] = focus.rotation;

      _c.current.position.set(x, y, z);
      _c.current.rotation.x = rx;
      _c.current.rotation.y = ry + Math.PI / 2;
      _c.current.rotation.z = rz;
      _c.current.translateZ(7.5);

      const { x: x1, y: y1, z: z1 } = _c.current.position;
      setCameraPos([x1, y1, z1]);
      const { x: rx1, y: ry1, z: rz1 } = _c.current.rotation;
      setCameraRot([rx1, ry1, rz1]);
    }
  }, [focus]);

  useFrame(({ gl, scene, camera }, clock) => {
    if (!isLoaded) {
      return;
    }
    const [_x, _y, _z] = cameraPos;
    const { x, y, z } = camera.position;
    const __x = x + (_x - x) / 15;
    const __y = y + (_y - y) / 15;
    const __z = z + (_z - z) / 15;
    camera.position.set(__x, __y, __z);

    const w = document.getElementById("react-root").offsetWidth;
    const h = document.getElementById("react-root").offsetHeight;

    camera.setViewOffset(w, h, 0, -h * 0.35, w, h);

    const [_rx, _ry, _rz] = cameraRot;
    const { x: rx, y: ry, z: rz } = camera.rotation;

    const __rx = rx + (_rx - rx) / 25;
    const __ry = ry + (_ry - ry) / 25;
    const __rz = rz + (_rz - rz) / 25;

    camera.rotation.x = __rx;
    camera.rotation.y = __ry;
    camera.rotation.z = __rz;
  });
  return <></>;
}

function App() {
  const [products, setProducts] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [text, setText] = useState({});
  const [nav, setNav] = useState([]);
  const [focus, setFocus] = useState(null);
  const [focusProduct, setFocusProduct] = useState(null);
  const [showLabels, setShowLabels] = useState(false);
  const annotationPos = useRef([]);

  function updateAnnotationPos(pos) {
    annotationPos.current = pos;
  }

  useEffect(() => {
    if (document.getElementById("react-root-container").offsetWidth > 1600) {
      document.getElementById("react-root-container").style.height = "678px";
    }
    let jsonPath;
    jsonPath = window.JSONPATH || "/home.json";
    get(jsonPath)
      .then((result) => {
        setProducts(
          result.data.products.sort((a, b) => (Math.random() > 0.5 ? -1 : 1))
        );
        setNav(result.data.navigation);
        setText(result.data.text);
      })
      .catch((err) => console.log({ err }));
  }, []);

  let bloomLightRef1 = useRef();
  let bloomLightRef2 = useRef();
  let bloomLightRef3 = useRef();

  return (
    <>
      <div className="canvas">
        <Canvas
          resize={{ polyfill: ResizeObserver }}
          shadows={{ type: PCFSoftShadowMap }}
          gl={{
            toneMapping: ReinhardToneMapping,
            alpha: true,
            pixelRatio: window.innerWidth < 500 ? 2 : 1,
          }}
          linear={true}
          camera={{ position: [30, 20, 18], rotation: [0, 2, 40] }}
          onPointerMissed={(e) => {
            setFocus(null);
            setFocusProduct(null);
          }}
        >
          <CameraComp focus={focus} isLoaded={isLoaded} />

          <ambientLight color="#451CFF" intensity={4} />
          <spotLight
            castShadow
            position={[10, 3, 12]}
            shadow-bias={-0.004}
            color="#451CFF"
            intensity={5.3}
            penumbra={0.5}
          />
          <spotLight
            position={[-12, 0, 12]}
            color="#FF0000"
            intensity={7}
            penumbra={1}
            shadow-bias={-0.004}
            castShadow
          />
          <spotLight
            position={[-13, 2, -20]}
            color="white"
            intensity={2}
            penumbra={1}
            shadow-bias={-0.004}
            castShadow
            ref={bloomLightRef1}
          />
          <directionalLight
            position={[8, -3, -3]}
            color="#fff"
            intensity={8}
            ref={bloomLightRef2}
          />
          <directionalLight
            position={[-8, -3, -4]}
            color="#fff"
            intensity={8}
            ref={bloomLightRef3}
          />

          {products.length > 0 && (
            <Suspense fallback={false}>
              <City
                scale={1}
                position={[0, -2, 1]}
                products={products}
                setFocus={setFocus}
                focusProduct={products.indexOf(focusProduct)}
                focusUrl={focusProduct?.target}
                setFocusProduct={setFocusProduct}
                updateAnnotationPos={updateAnnotationPos}
                bloomLightRef1={bloomLightRef1}
                bloomLightRef2={bloomLightRef2}
                bloomLightRef3={bloomLightRef3}
                onLoad={() => {
                  setShowLabels(true);
                  setIsLoaded(true);
                }}
              />
            </Suspense>
          )}
        </Canvas>
      </div>

      {!isLoaded && <LoadingIndicator />}
      {products.length > 0 && (
        <Annotations
          pos={annotationPos}
          products={products}
          focusProduct={focusProduct}
          show={showLabels}
        />
      )}
      <div className="threejs_ui">
        <h1>{text.heading}</h1>
        <p>{text.blurb}</p>
        <Dropdown nav={nav} />
        <button
          className={`backButton ${focus ? "show" : ""}`}
          onClick={() => {
            setFocus(null);
            setFocusProduct(null);
          }}
        >
          <img
            src="./3dshowcase/images/grid-3x2-gap-fill.svg"
            alt="Back to overview"
          />
        </button>
      </div>
    </>
  );
}

export default App;

function Dropdown({ nav }) {
  const thisRef = useRef();
  const mouseOutRef = useRef();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    document.addEventListener("click", (e) => {
      if (thisRef.current && !thisRef.current.contains(e.target)) {
        setExpanded(false);
      }
    });
  }, []);

  if (nav.length === 0) {
    return <></>;
  }

  return (
    <div className={`dropdown${expanded ? " expanded" : ""}`} ref={thisRef}>
      <button
        className="active"
        onClick={() => {
          setExpanded(true);
        }}
      >
        {nav.find((n) => n.active).title}
      </button>
      <ul
        onMouseMove={() => {
          if (mouseOutRef.current) {
            window.clearTimeout(mouseOutRef.current);
          }
        }}
        onMouseOut={() => {
          if (mouseOutRef.current) {
            window.clearTimeout(mouseOutRef.current);
          }
          mouseOutRef.current = window.setTimeout(() => {
            setExpanded(false);
          }, 300);
        }}
      >
        {nav.map((n) => (
          <li key={n.title}>
            <a href={n.target} className="showcase-theme">
              {n.title}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Annotations({ pos, products, focusProduct, show }) {
  const ref = useRef();
  useEffect(() => {
    if (!ref.current || products.length < 1) {
      return;
    }
    let c = ref.current.children;
    function animate() {
      const h = document.getElementById("react-root").offsetHeight;
      if (pos.current.length > 0) {
        Array.from(c).forEach((div, i) => {
          let px = (pos.current[i].x / 2 + 0.5) * 100;
          let py = (pos.current[i].y / 2 + 0.5) * -100;
          div.style.top = `${py + 10}%`;
          div.style.left = `${px}%`;
          div.style.transform = `translate3d(${-50 + pos.current[i].x * 80}%,${
            h * 0.86
          }px,0)`;
        });
      }
      window.requestAnimationFrame(animate);
    }
    let id = window.requestAnimationFrame(animate);
    return () => {
      window.cancelAnimationFrame(id);
    };
  }, [products]);

  return (
    <div
      className={`threejs_annotations ${focusProduct ? "focused" : ""} ${
        show ? "" : "hidden"
      }`}
      ref={ref}
    >
      {products.map((p) => (
        <div
          key={p.label}
          className={focusProduct?.label === p.label ? "focus" : ""}
        >
          <a href={p.target} className="showcase-product">
            {p.label}
          </a>
        </div>
      ))}
    </div>
  );
}

function LoadingIndicator() {
  return (
    <div className="threejs__loading_indicator">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="80px"
        height="80px"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid"
      >
        <circle
          cx="50"
          cy="50"
          r="32"
          stroke-width="8"
          stroke="#e1091d"
          stroke-dasharray="50.26548245743669 50.26548245743669"
          fill="none"
          stroke-linecap="round"
        ></circle>
      </svg>
    </div>
  );
}
